/**
 * /runtime-cockpit — Surface Layer (/01)
 *
 * DevHub operator cockpit: live worker/guardrail services, the chat & execution
 * queue (real action_frames + action_results), and secret-slot status — composed
 * from the honest-state foundation primitives.
 *
 * Plane-guarded to the DevHub (control) plane via routePolicy `PLANE_RESTRICTED`
 * `/runtime-cockpit` → a non-operator deep-linking here is redirected to their own
 * dashboard (no cross-hub leak).
 *
 * LIVE sources (this lane replaced the samples):
 *   • Chat & queue — authed `useConversations` BFF GET (honest Empty/Blocked w/o a session).
 *   • Services     — `useServiceControlState` over the dev-only local-runtime bridge
 *                    (/__local/service-control). Live in the operator's LOCAL cockpit; the
 *                    deployed origin honestly reports the bridge as unreachable (the wall).
 *   • Secrets      — `useSecretSlots` authed BFF GET (status + redacted fingerprint ONLY;
 *                    the live answer may be a blocked-by-design state, surfaced honestly).
 * The dev-only harness (cockpit-preview.html) renders the SAME view with sample data for a
 * no-login render proof.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useConversations } from '@lib/persistence/useRuntimeData';
import { useServiceControlState, useSecretSlots } from '@lib/persistence/useDevhubRuntime';
import { RuntimeCockpitView } from '../components/devhub-cockpit';
import '../styles/views.scss';

export const Route = createFileRoute('/runtime-cockpit')({
  component: RuntimeCockpitRoute,
});

function RuntimeCockpitRoute() {
  const { conversations, isLoading, tenantContextMissing } = useConversations(undefined, { pollMs: 5000 });
  const services = useServiceControlState();
  const secrets = useSecretSlots();

  const conversationsError = tenantContextMissing
    ? {
        code: 'tenant_context_missing',
        message: 'Sign in with an operator tenant to load the live runtime queue.',
      }
    : null;

  return (
    <RuntimeCockpitView
      conversations={conversations}
      services={services.services}
      secrets={secrets.slots}
      conversationsLoading={isLoading}
      conversationsError={conversationsError}
      servicesAvailable={services.available}
      servicesSource={services.source}
      secretsAuthRequired={secrets.authRequired}
      secretsBlocked={secrets.blocked}
      secretsBlockedReason={secrets.blockedReason}
      secretsLoading={secrets.isLoading}
      secretsError={secrets.error}
    />
  );
}
